/**
 * Scene graph visibility: outliner parent/child uses `parentId` (and optional `metadata.parentId`).
 * An object is shown only when it is visible and every ancestor is visible.
 */
export interface SceneObjectLike {
  id: string;
  parentId?: string | null;
  visible?: boolean;
  metadata?: { parentId?: string | null };
}

function parentIdOf(o: SceneObjectLike): string | null {
  const fromRoot = o.parentId ?? null;
  if (fromRoot) return fromRoot;
  const m = o.metadata?.parentId;
  return m ?? null;
}

export function buildEffectiveVisibleMap(objects: SceneObjectLike[]): Map<string, boolean> {
  const byId = new Map(objects.map((o) => [o.id, o]));
  const memo = new Map<string, boolean>();
  const walk = (id: string): boolean => {
    if (memo.has(id)) return memo.get(id)!;
    const o = byId.get(id);
    if (!o) {
      memo.set(id, true);
      return true;
    }
    if (o.visible === false) {
      memo.set(id, false);
      return false;
    }
    const pid = parentIdOf(o);
    if (!pid) {
      memo.set(id, true);
      return true;
    }
    const ok = walk(pid);
    memo.set(id, ok);
    return ok;
  };
  for (const o of objects) walk(o.id);
  return memo;
}
